<template>
  <div
    v-if="draftCardCreateView"
    ref="draftCardInput"
    v-click-outside="addFromOutside"
    class="card-create-publish-button"
    @mouseup="blurElementHandler"
  >
    <div
      class="title-input-wrap"
      @wheel.stop.prevent
      @touchmove.stop.prevent
      @scroll.stop.prevent
      @mouseup.stop
      @click.stop
    >
      <vue-scroll
        :ops="{
          scrollPanel: { scrollingX: false },
          rail: { size: '8px', gutterOfSide: '3px' },
          bar: { size: '8px', keepShow: true },
        }"
        class="title-input"
      >
        <AutoExpandTextarea
          ref="draftCardTitleInput"
          :value="draftCardTitle"
          class="draft-list-card usn"
          contenteditable="true"
          :text-value="draftCardTitle"
          :initial-height="textareaHeight"
          :placeholder="placeholderText"
          :textarea-class="textareaClass"
          @enter="createCard"
          @input="inputHandler"
          @esc="escHandler"
          @scrollBoardListToEnd="scrollBoardListToEnd"
          @mousedown="focusElementHandler"
          @mouseup="blurElementHandler"
        />
      </vue-scroll>
    </div>
    <div class="card-footer-buttons flex-row-reverse min-h-6">
      <ActionButtons
        class="draft-card-action-buttons"
        :stop-cancel-propagation="true"
        :stop-confirm-propagation="true"
        @confirm="createCard"
        @cancel="hideCreateView"
      />
      <ApplyTemplate
        v-if="!isCardToCreateTypeEpic"
        :hide-button-text="true"
        :button-custom-class="'hover:bg-important-hover'"
        :enable-tooltip="true"
        @selectTemplate="handleTemplateSelect"
      />
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import EventBus from '@/utilities/eventBus';
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea';
import getDashedTitle from '@/utilities/getDashedTitle';
import styleVariables from '@/assets/scss/variables.module.scss';
import { convertStyleToNumber } from '@/utilities/rootStyles';
import customIconsMixin from '@/mixins/customIconsMixin';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import { groupByOption } from '@/types/groupByOption';
import ApplyTemplate from '@/components/widgets/ApplyTemplate';
import {
  getQuickCardCreationDraftKey,
  getDraft,
  setDraft,
  removeDraft,
  getDraftCardTitleKey,
} from '@/utilities/localDrafts';
import { browserStorageSetItem } from '@/utilities/browser-storage.util';
import hideDropdownsMixin from '@/mixins/hideDropdownsMixin';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { ResourceType } from '@/types/resources';
import useGenerateTagsFromIds from '@/utilities/composables/useGenerateTagsFromIds.ts';
import ActionButtons from '@/components/widgets/ActionButtons.vue';
import { getSprintKey } from '@/utilities/sprintKeys';

export default {
  name: 'DraftCardCreate',
  components: {
    AutoExpandTextarea,
    ApplyTemplate,
    ActionButtons,
  },
  mixins: [customIconsMixin, elementFocusMixin, hideDropdownsMixin],
  props: {
    listId: {
      type: [String, Number],
      required: true,
    },
    listTitle: {
      type: String,
      required: true,
    },
    quickView: {
      type: Boolean,
      required: false,
      default: false,
    },
    boardId: {
      type: String,
      required: false,
      default: '',
    },
    previewProjectBoards: {
      type: Boolean,
      required: false,
      default: false,
    },
    parentListId: {
      type: [String, Number],
      default: '',
    },
  },

  emits: ['scrollBoardListToEnd', 'hideCreateCardView'],

  setup() {
    const { embeddedBoardAttributes, embed } = useBoardEmbed();
    const { generateTagsFromIds } = useGenerateTagsFromIds();

    return {
      embeddedBoardAttributes,
      embed,
      generateTagsFromIds,
    };
  },

  data() {
    return {
      draftCardTitle: '',
      preventCreateCard: false,
      draftTimeout: null,
    };
  },

  computed: {
    placeholderText() {
      return this.translate('enterResourceTitle', {
        type: this.translate(this.cardToCreateType).toLowerCase(),
      });
    },
    cardToCreateType() {
      return this.currentBoardId === this.$constants.epicsBoardId
        ? ResourceType.Epic
        : ResourceType.Card;
    },

    isCardToCreateTypeEpic() {
      return this.cardToCreateType === ResourceType.Epic;
    },

    textareaClass() {
      const aditionalClass =
        this.isCardsLayoutList || this.isCardsLayoutTimeline ? 'whitespace-nowrap pl-0' : '';
      return `draft-list-card cozy-list-draft-card ${aditionalClass}`;
    },
    isCardsLayoutTimeline() {
      if (this.embed) {
        return this.embeddedBoardAttributes.layout === this.$constants.timeline;
      }
      return this.$store.getters.cardsLayout() === this.$constants.timeline;
    },
    isCardsLayoutList() {
      if (this.embed) {
        return this.embeddedBoardAttributes.layout === this.$constants.list;
      }
      return this.$store.getters.cardsLayout() === this.$constants.list;
    },
    listDraftCardCreateView() {
      return this.$store.getters.getListDraftCardCreateView;
    },
    draftCardCreateView() {
      if (this.parentListId !== '') {
        return `${this.parentListId}-${this.listId}` === this.listDraftCardCreateView;
      }
      return this.listId === this.listDraftCardCreateView;
    },
    currentBoardTitle() {
      return this.currentBoard.title;
    },
    epicsBoardRoute() {
      return this.$route.name === this.$constants.epicsBoardRoute.name;
    },
    isSprints() {
      return (
        this.$route.name === this.$constants.routeNames.sprintsRouteName ||
        this.$route.name === this.$constants.routeNames.sprint ||
        this.boardId.includes('s')
      );
    },
    currentProjectId() {
      return this.$store.getters.getCurrentProjectId;
    },
    currentBoardId() {
      return this.currentBoard?.id;
    },
    projectId() {
      return this.currentBoard?.project_id;
    },
    currentBoard() {
      if (this.embed) {
        return this.boards[this.embeddedBoardAttributes.boardId];
      }
      return this.quickView ? this.boards[this.boardId] : this.$store.getters.currentBoard;
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    boards() {
      return this.$store.getters.getFlatBoards;
    },
    textareaHeight() {
      return convertStyleToNumber(styleVariables.cardTitleLineHeight) * 2;
    },
    boardSortedBy() {
      return this.embed
        ? this.embeddedBoardAttributes.sortBy
        : this.$store.getters.getBoardSortBy(this.currentBoardId);
    },

    boardGroupBy() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.groupBy || groupByOption.status;
      }
      return this.$store.getters.getBoardGroupBy(this.currentBoardId);
    },

    boardSubGroupBy() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.subGroupBy || groupByOption.status;
      }
      return this.$store.getters.getBoardSubGroupBy(this.currentBoardId) || groupByOption.status;
    },

    groupByAssignee() {
      return this.boardGroupBy === groupByOption.assignee;
    },

    subGroupByAssignee() {
      return this.boardSubGroupBy === groupByOption.assignee && this.subGroupByEnabled;
    },

    groupByTag() {
      return this.boardGroupBy === groupByOption.tag;
    },

    subGroupByTag() {
      return this.boardSubGroupBy === groupByOption.tag && this.subGroupByEnabled;
    },

    groupByStatus() {
      return this.boardGroupBy === groupByOption.status;
    },

    subGroupByStatus() {
      return this.boardSubGroupBy === groupByOption.status && this.subGroupByEnabled;
    },

    groupByPriority() {
      return this.boardGroupBy === groupByOption.priority;
    },

    groupByEpic() {
      return this.boardGroupBy === groupByOption.epic;
    },

    boardRoute() {
      return this.$route.name === 'board';
    },

    sprintRoute() {
      return this.$route.name === 'sprint';
    },

    boardOrSprintRoute() {
      return this.boardRoute || this.sprintRoute;
    },

    notagsListId() {
      return this.listId.includes('notags');
    },

    noProjectListId() {
      return this.listId.includes('noproject');
    },

    currentTeamId() {
      return this.$store.getters.getTeamID;
    },

    currentQuickCard() {
      return this.$store.getters.getQuickCard;
    },

    subGroupByEnabled() {
      return this.$store.getters.getSubGroupByEnabled(this.currentBoardId);
    },

    statusListId() {
      // returns current list id or first list id if group by is different than status
      const firstListId = this.sprintRoute
        ? this.currentBoard?.lists[0]?.id
        : (this.currentBoard?.list_order?.[0] ?? null);
      const shouldUseGroupByStatus =
        this.groupByStatus ||
        this.subGroupByStatus ||
        (!this.boardRoute && !this.sprintRoute && !this.epicsBoardRoute);

      let listId = this.listId;
      if (this.subGroupByEnabled && this.groupByStatus) {
        listId = this.parentListId;
      }

      return shouldUseGroupByStatus ? listId : firstListId;
    },

    getCardPriority() {
      let listId = this.listId;
      if (this.subGroupByEnabled && this.groupByPriority) {
        listId = this.parentListId;
      }
      return this.groupByPriority && (this.boardOrSprintRoute || this.epicsBoardRoute) ? listId : 0;
    },

    getCardMembers() {
      let listId = this.listId;
      if (this.subGroupByEnabled && this.groupByAssignee) {
        listId = this.parentListId;
      }

      return (this.groupByAssignee || this.subGroupByAssignee) &&
        (this.boardOrSprintRoute || this.epicsBoardRoute) &&
        !listId.includes('unassigned')
        ? [{ user_id: listId }]
        : [];
    },

    isTagsGroupingEnabled() {
      return (
        (this.groupByTag || this.subGroupByTag) &&
        (this.boardOrSprintRoute || this.epicsBoardRoute) &&
        !this.notagsListId
      );
    },

    getTags() {
      return this.$store.getters.getTags || {};
    },

    getCardTags() {
      let listId = this.listId;
      if (this.subGroupByEnabled && this.groupByTag) {
        listId = this.parentListId;
      }
      return this.isTagsGroupingEnabled ? [this.getTags[listId]] : [];
    },

    isGroupByStatusDisabledOnBoard() {
      return !this.groupByStatus && (this.boardOrSprintRoute || this.epicsBoardRoute);
    },

    draftCardTitleKey() {
      return getDraftCardTitleKey(
        this.currentTeamId,
        this.projectId,
        this.currentBoardId,
        this.listId,
        'newcard'
      );
    },
  },
  mounted() {
    EventBus.$on('focusDraftCardInput', this.focusListener);
    if (this.$refs.draftCardTitleInput) {
      this.$refs.draftCardTitleInput.focusInput();
    }
    this.draftCardTitle = getDraft(this.draftCardTitleKey) || '';
  },
  beforeUnmount() {
    EventBus.$off('focusDraftCardInput', this.focusListener);
    this.removeCreateCardDraftEvent();
    if (this.draftCardTitle) {
      setDraft(this.draftCardTitleKey, this.draftCardTitle);
    }
  },
  updated() {
    EventBus.$emit('scrollToEnd', this.listId);
  },
  methods: {
    escHandler() {
      this.hideCreateView(false);
    },
    inputHandler(event) {
      this.draftCardTitle = event.target.value;
      this.scrollToEnd();
      clearTimeout(this.draftTimeout);
      if (this.draftCardTitle) {
        this.draftTimeout = setTimeout(() => {
          setDraft(this.draftCardTitleKey, this.draftCardTitle);
        }, 500);
      }
    },

    focusListener({ listId }) {
      if (listId !== this.listId) return;
      this.$nextTick(() => {
        if (this.$refs.draftCardTitleInput) {
          this.$refs.draftCardTitleInput.focusInput();
        }
      });
    },

    updateCardIdInCardsOrder(localCardId, cardId, listId) {
      const listCards = [...this.$store.getters.getListCards[listId]];

      const index = listCards.indexOf(localCardId);
      if (index > -1) {
        listCards.splice(index, 1, cardId);
        this.$store.dispatch('setSingleListInListCards', {
          listId,
          value: listCards,
        });
      }
    },

    addLocalCard(localCardId) {
      const listId = this.statusListId;

      this.$store.dispatch('addCardToListCards', {
        listId: listId,
        cardId: localCardId,
      });
      this.$store.dispatch('addCardToCards', {
        card: {
          title: this.draftCardTitle,
          ...(localCardId && { id: localCardId, localCardId: true }),
          ...(!this.isSprints && { board_id: this.currentBoardId }),
          ...(this.isSprints && { sprint_id: this.currentBoardId }),
          list_id: listId,
          list_title: this.listTitle,
          members: this.getCardMembers,
          child_cards: [],
          child_card_order: [],
          checklists: [],
          checklist_order: [],
          priority: this.getCardPriority,
          checklist_item_order: {},
          parent_card: {},
          user_id: this.$store.getters.getUserId,
          tags: this.getCardTags,
          is_watching: true,
          ...(this.groupByEpic &&
            !this.noProjectListId && { epic_id: this.listId.split('-')[1] || '' }),
          type: this.cardToCreateType,
        },
      });
      if (this.isGroupByStatusDisabledOnBoard) {
        this.$store.dispatch('addCardToGroupByList', {
          boardId: this.currentBoardId,
          cardId: localCardId,
          listId:
            this.groupByEpic && !this.noProjectListId
              ? this.listId.split('-')[1] || ''
              : this.listId,
          groupBy: this.boardGroupBy,
        });
      }

      this.draftCardTitle = '';
    },

    hideCreateView(shouldRemoveDraft = true) {
      this.$store.dispatch('setListDraftCardCreateView', null);
      if (shouldRemoveDraft) {
        removeDraft(this.draftCardTitleKey);
        this.draftCardTitle = '';
      }
      this.$emit('hideCreateCardView');
    },

    addCardToGroupByList(card, listId, groupBy) {
      this.$store.dispatch('addCardToGroupByList', {
        boardId: card.sprint_id ? getSprintKey(card.project_id, card.sprint_id) : card.board_id,
        cardId: card.id,
        listId,
        groupBy,
      });
    },

    updateGroupByListCardsOrder(card, localCardId, listId) {
      if (this.groupByStatus || !(this.boardOrSprintRoute || this.epicsBoardRoute)) {
        this.addCardToGroupByList(card, 'unassigned', groupByOption.assignee);
        this.addCardToGroupByList(card, `notags`, groupByOption.tag);
        this.addCardToGroupByList(card, 0, groupByOption.priority);
        this.addCardToGroupByList(card, groupByOption.none, groupByOption.none);
        this.addCardToGroupByList(card, 'noproject', groupByOption.epic);
      }
      if (this.isGroupByStatusDisabledOnBoard) {
        switch (this.boardGroupBy) {
          case groupByOption.assignee:
            this.addCardToGroupByList(card, listId, groupByOption.assignee);
            this.addCardToGroupByList(card, `notags`, groupByOption.tag);
            this.addCardToGroupByList(card, 0, groupByOption.priority);
            this.addCardToGroupByList(card, groupByOption.none, groupByOption.none);
            this.addCardToGroupByList(card, 'noproject', groupByOption.epic);
            break;
          case groupByOption.tag:
            this.addCardToGroupByList(card, 'unassigned', groupByOption.assignee);
            this.addCardToGroupByList(card, listId, groupByOption.tag);
            this.addCardToGroupByList(card, 0, groupByOption.priority);
            this.addCardToGroupByList(card, groupByOption.none, groupByOption.none);
            this.addCardToGroupByList(card, 'noproject', groupByOption.epic);
            break;
          case groupByOption.priority:
            this.addCardToGroupByList(card, 'unassigned', groupByOption.assignee);
            this.addCardToGroupByList(card, `notags`, groupByOption.tag);
            this.addCardToGroupByList(card, listId, groupByOption.priority);
            this.addCardToGroupByList(card, groupByOption.none, groupByOption.none);
            this.addCardToGroupByList(card, 'noproject', groupByOption.epic);
            break;
          case groupByOption.none:
            this.addCardToGroupByList(card, 'unassigned', groupByOption.assignee);
            this.addCardToGroupByList(card, `notags`, groupByOption.tag);
            this.addCardToGroupByList(card, 0, groupByOption.priority);
            this.addCardToGroupByList(card, listId, groupByOption.none);
            this.addCardToGroupByList(card, 'noproject', groupByOption.epic);
            break;
          case groupByOption.epic:
            this.addCardToGroupByList(card, 'unassigned', groupByOption.assignee);
            this.addCardToGroupByList(card, `notags`, groupByOption.tag);
            this.addCardToGroupByList(card, 0, groupByOption.priority);
            this.addCardToGroupByList(card, groupByOption.none, groupByOption.none);
            this.addCardToGroupByList(
              card,
              this.noProjectListId ? listId : this.listId.split('-')[1] || '',
              groupByOption.epic
            );
            break;
          default:
            break;
        }
      }
      this.$store.dispatch('removeCardFromGroupByList', {
        boardId: this.currentBoardId,
        epicId: card.epic?.id,
        cardId: localCardId,
        listId: listId,
      });
    },
    removeCreateCardDraftEvent() {
      EventBus.$off('removeCreateCardDraft', this.handleRemoveCreateCardDraft);
    },
    handleRemoveCreateCardDraft(shouldRemoveDraft) {
      if (shouldRemoveDraft) {
        this.draftCardTitle = '';
      }
      this.removeCreateCardDraftEvent();
    },
    createCard($event, focus = true) {
      const { draftCardTitle: cardTitle } = this;
      if (this.preventCreateCard) {
        this.preventCreateCard = false;
        // emit event to prevent creating card when quick card modal is opened
        EventBus.$emit('preventCreateCard');

        // remove title draft when card is created from quick card modal
        EventBus.$on('removeCreateCardDraft', this.handleRemoveCreateCardDraft);
        return;
      }
      if (!cardTitle?.trim().length) return;
      const localCardId = nanoid(6);
      const boardId = this.currentBoardId;
      this.addLocalCard(localCardId);
      if (focus) {
        this.focusListener({ listId: this.listId });
      }
      if (this.isShiftKey($event)) {
        this.hideCreateView();
      }

      const listId =
        this.isSprints || this.isSprint
          ? this.$store.getters.lists[this.statusListId].orginalId
          : this.statusListId;

      this.$store
        .dispatch('createCard', {
          card: {
            ...(!this.isSprints && { board_id: boardId }),
            ...(this.isSprints && { sprint_id: this.currentBoard.orginalId }),
            ...(this.isSprints && { project_id: this.currentBoard.project_id }),
            list_id: listId,
            title: cardTitle.trim(),
            priority: this.getCardPriority,
            members: this.getCardMembers,
            ...(this.groupByEpic &&
              !this.noProjectListId && { epic_id: this.listId.split('-')[1] || '' }),
          },
          type: this.cardToCreateType,
        })
        .then(({ card }) => {
          const cardListId = this.isSprints ? `${this.currentBoard.id}-${listId}` : listId;

          if (this.isTagsGroupingEnabled) {
            this.$store.dispatch('addTagsToCard', {
              cardId: card.id,
              ids: [this.listId],
            });
          }
          if (this.isShiftKey($event)) {
            this.$store.dispatch('setCardAutoFocus', true);
            this.openCard(card);
          }
          removeDraft(this.draftCardTitleKey);

          const newCard = {
            ...card,
            is_watching: true,
            priority: this.getCardPriority,
            members: this.getCardMembers,
            tags: this.getCardTags,
            ...(this.cardToCreateType === ResourceType.Epic && { type: ResourceType.Epic }),
          };
          this.$store.dispatch('addCardToCards', { card: newCard });
          this.updateCardIdInCardsOrder(localCardId, card.id, cardListId);
          this.$store.dispatch('removeCardFromCards', localCardId);
          this.updateGroupByListCardsOrder(newCard, localCardId, this.listId);
          if (this.subGroupByEnabled && this.parentListId !== '') {
            this.updateGroupByListCardsOrder(newCard, localCardId, this.parentListId);
          }
          this.displayHiddenByFilterNotification(card);
          if (this.boardSortedBy !== 'manual') {
            const notificationMessage = this.translate('createdCardMovedSortOrder');
            this.showCreatedCardNotification(notificationMessage, card);
          }
          if (this.isGroupByStatusDisabledOnBoard) {
            const notificationMessage = this.translate('createCardGroupBy', {
              list: card.list_title,
            });
            this.showCreatedCardNotification(notificationMessage, card);
          }
        })
        .catch(() => {
          this.$store.dispatch('removeCardFromListCards', {
            listId,
            cardId: localCardId,
          });
          this.$store.dispatch('removeCardFromCards', localCardId);
        });
    },

    isShiftKey(event) {
      return event && event.shiftKey;
    },

    openCard(card) {
      this.$store.dispatch('setDisplayedCards', [card.id]);
      const dashedCardTitle = getDashedTitle(card.title);
      const routeName = card.sprint_id ? 'sprintcard' : 'card';
      this.$router.push({
        path: `/${this.teamSlug}/${routeName}-${card.id}-${dashedCardTitle}`,
        query: { userNavigated: true },
      });
    },

    addFromOutside() {
      if (this.isFocusElement()) return;
      if (this.draftCardTitle) {
        this.createCard({}, false);
      }
      this.hideCreateView();
    },

    scrollToEnd() {
      EventBus.$emit('scrollToEnd', this.listId);
    },

    scrollBoardListToEnd() {
      this.$emit('scrollBoardListToEnd', this.listId);
    },

    displayHiddenByFilterNotification(card) {
      const cardParent = card.sprint_id
        ? getSprintKey(card.project_id, card.sprint_id)
        : card.board_id;
      if (!this.$store.getters.boardsPills(cardParent).length) return;

      const notificationMessage = this.translate('createdCardHiddenByFilter');
      this.showCreatedCardNotification(notificationMessage, card);
    },

    showCreatedCardNotification(message, card) {
      this.$store.dispatch('addUiNotification', {
        message,
        status: this.$constants.uiNotificationStatuses.warning,
        labels: [
          {
            text: this.translate('open'),
            type: this.$constants.uiNotificationElements.button,
            callback: this.openCard,
            params: card,
            buttonStyle: 'important',
          },
        ],
      });
    },

    displayQuickCardModal(isEnterKey, fromTemplate = false) {
      this.$store.dispatch('setQuickCard', true);
      if (fromTemplate && isEnterKey) {
        requestAnimationFrame(() => {
          this.addFromOutside();
        });
      }
    },

    handleTemplateSelect(template, isEnterKey) {
      this.preventCreateCard = true;
      if (!template) {
        this.displayQuickCardModal(isEnterKey, true);
        return;
      }

      const { currentQuickCard: quickCard } = this;
      const { currentBoard: board } = this;
      const cardTitle = this.draftCardTitle.trim() || template.card_title;

      const isSprint = board.id.includes('s');

      const quickCardContent = {
        ...quickCard,
        ...(board.id && !isSprint && { boardId: board.id }),
        ...(board.id && isSprint && { sprintId: board.orginalId }),
        ...(template.card_content && { content: template.card_content }),
        ...(this.statusListId && { listId: this.statusListId }),
        ...(template.card_members && { members: template.card_members }),
        ...(template.card_priority && { priority: template.card_priority }),
        ...(template.card_tags?.[0]?.ids && {
          tags: this.generateTagsFromIds(template.card_tags[0].ids, this.currentBoard?.project_id),
        }),
        ...(cardTitle && { title: cardTitle }),
        ...(board && { projectId: board.project_id, board }),
        ...(template.card_child_cards && { childCards: template.card_child_cards }),
      };
      browserStorageSetItem(getQuickCardCreationDraftKey(this.currentTeamId), quickCardContent);
      this.displayQuickCardModal(isEnterKey, true);
    },
  },
};
</script>

<style lang="scss">
.card-create-publish-button {
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  width: $columnWidth;
  border: 1px solid $gray300;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  background: $stPrimaryBackgroundColor;
  @apply bg-important-bg text-text-default border-important-focusedBorder;

  .title-input-wrap {
    display: flex;
    margin-top: 10px;
    textarea {
      @apply bg-important-bg text-text-default;
    }
  }

  .draft-list-card {
    font-size: 14px;
    line-height: $cardTitleLineHeight;
    font-weight: normal;
    color: $gray900;

    &.cozy-list-draft-card {
      padding: 0px 15px;
    }
  }

  .title-input {
    display: flex;
    flex-direction: column;
    min-height: $cardTitleLineHeight * 2;
    max-height: $cardTitleLineHeight * 3;
  }
}
</style>
